/**
 * PayGO MUI theme — maps CSS design tokens to Material UI.
 * Brand: navy shell + green actions (not default MUI purple).
 * Breakpoints: full SOTA ladder (phone → desktop) via shared/theme/breakpoints.ts
 */
import { createTheme } from "@mui/material/styles";
import { PAYGO_BREAKPOINTS, SOTA_VIEWPORTS } from "./breakpoints";

export const paygoTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#15803d",
      dark: "#166534",
      light: "#16a34a",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#1e2a4a",
      dark: "#0f172a",
      light: "#263457",
      contrastText: "#f1f5f9",
    },
    error: { main: "#dc2626" },
    warning: { main: "#d97706" },
    info: { main: "#2563eb" },
    success: { main: "#16a34a" },
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
    },
    text: {
      primary: "#0f172a",
      secondary: "#64748b",
    },
    divider: "#e2e8f0",
  },
  typography: {
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    fontSize: 14,
    h1: { fontSize: "1.5rem", fontWeight: 700 },
    h2: { fontSize: "1.25rem", fontWeight: 700 },
    h3: { fontSize: "1.1rem", fontWeight: 600 },
    body1: { fontSize: "0.875rem" },
    body2: { fontSize: "0.75rem" },
    button: { textTransform: "none", fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  breakpoints: {
    values: { ...PAYGO_BREAKPOINTS },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 40,
          [`@media (max-width:${SOTA_VIEWPORTS.largePhone}px)`]: { minHeight: 44 },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          [`@media (max-width:${SOTA_VIEWPORTS.largePhone}px)`]: { minWidth: 44, minHeight: 44 },
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: "small", fullWidth: true },
    },
    MuiCard: {
      defaultProps: { variant: "outlined" },
    },
    MuiDialog: {
      defaultProps: { fullWidth: true, maxWidth: "sm" },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          [`@media (max-width:${SOTA_VIEWPORTS.largePhone}px)`]: {
            paddingLeft: 8,
            paddingRight: 8,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#1e2a4a",
          color: "#cbd5e1",
          borderRight: "none",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          marginInline: 8,
          "&.Mui-selected": {
            backgroundColor: "rgba(21, 128, 61, 0.22)",
            color: "#ffffff",
            "&:hover": { backgroundColor: "rgba(21, 128, 61, 0.3)" },
            "& .MuiListItemIcon-root": { color: "#ffffff" },
          },
          "&:hover": { backgroundColor: "#263457" },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        /* Legacy pg-* layout helpers — keep structure, neutralize old card chrome */
        ".pg-list-header, .pg-office-header": {
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: "20px",
        },
        ".pg-list-header h2, .pg-office-header h2, .pg-breadcrumb + h2": {
          fontSize: "1.25rem",
          fontWeight: 700,
          margin: 0,
        },
        ".pg-list-header p, .pg-office-header p, .pg-text-muted": {
          color: "#64748b",
          margin: "4px 0 0",
          fontSize: "0.875rem",
        },
        ".pg-state": {
          display: "grid",
          placeItems: "center",
          gap: "12px",
          padding: "48px 16px",
          textAlign: "center",
          color: "#64748b",
        },
        ".pg-stats-row": {
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: "12px",
          marginBottom: "16px",
        },
        ".pg-card": {
          /* superseded by MUI Card via AppCard — neutralize if leftover class used */
          background: "transparent",
          border: "none",
          boxShadow: "none",
          padding: 0,
        },
        [`@media (max-width: ${SOTA_VIEWPORTS.largePhone}px)`]: {
          ".pg-list-header, .pg-office-header": {
            flexDirection: "column",
            alignItems: "stretch",
          },
        },
      },
    },
  },
});
